import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { API_BASE } from './api-base';
import { wixHttpPost } from './wix-http';
import { useGenericBi } from './use-generic-bi';
import { usePageLoadBi } from './use-page-load-bi';
import { useLoadBi } from './use-load-bi';
import { useBasePath } from './use-base-path';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { waitForRecipientVisible } from './wait-for-recipient';
import { useTranslation } from './i18n';
import { isValidPhone } from './phone-validation';
import { COUNTRY_DIAL, countryOptions, defaultCountryIso } from './country-codes';
import { useGeoCountry } from './geo-country';
import { readPhonePrefill } from './phone-prefill';
import { wasCodeSent } from './phone-verification-result';
import { sendVerificationCode } from './send-verification-code';
import { useSendCodeBi } from './use-send-code-bi';

/** Exported so the page can fire its page-view with the SAME src:172 identity its clicks report. */
export const PHONE_VERIFY_PAGE = {
  page_name: 'verify_phone_number_page',
  page_version: 'v3_onboarding_verify_phone_number',
} as const;
const PAGE = PHONE_VERIFY_PAGE;

/**
 * `onCodeSent` takes over from the push to `/onboarding/code`, and takes the number and the
 * verificationId with it. The standalone page passes them in the URL, which is why this exists: an
 * instrumented page reports its own location, so those params reach every analytics tag on it (see
 * `CodeVerificationInput`). A caller that keeps them in memory hands them over here instead.
 */
export type CodeSentHandler = (sent: { phone: string; method: 'sms' | 'whatsapp'; verificationId: string }) => void;

export function usePhoneVerification({ onCodeSent }: { onCodeSent?: CodeSentHandler } = {}) {
  const router = useRouter();
  const basePath = useBasePath();
  const { activeConversationId, setActiveConversationId } = useActiveConversation();
  // The thread this was opened over — the URL is authoritative, the context is the fallback.
  const conversationId = typeof router.query.conversation === 'string' ? router.query.conversation : activeConversationId ?? '';
  const { t, i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi('onboarding-phone');
  const logSendCode = useSendCodeBi('onboarding-phone');

  usePageLoadBi(PAGE);

  // Localized + name-sorted, memoized per active locale so a language switch
  // relabels and re-sorts without rebuilding on every render.
  const countries = useMemo(() => countryOptions(i18n?.language), [i18n?.language]);

  // The selected country is keyed on its ISO code, not the dial code — several countries share a
  // dial code (+1 is US/CA/…), so keying on the dial would show the wrong country. Mirrors native.
  //
  // Three sources, strongest first: the user's own pick, the country their IP is in, the region of
  // their browser locale. IP outranks locale because the two disagree for anyone running an en-US
  // browser abroad — a common enough setup that those users were being handed +1. Both defaults are
  // '' / 'US' on the server and first render, so hydration matches; each corrects post-mount.
  const [pickedIso, setPickedIso] = useState('');
  const [localeIso, setLocaleIso] = useState('US');
  const geoIso = useGeoCountry();
  const iso = pickedIso || geoIso || localeIso;
  const dial = COUNTRY_DIAL[iso] ?? '1';

  // The picker is left to resolve the country itself; only the national part is seeded.
  const seededPhone = useRef(readPhonePrefill()).current;
  const [phone, setPhone] = useState(seededPhone ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Flips true on blur AND ~600ms after the user stops typing, so the invalid-number error appears
  // even on this single-field screen (which often never blurs) — mirrors the native debounce.
  const [touched, setTouched] = useState(false);

  const digits = phone.replace(/\D/g, '');
  const full = `+${dial}${digits}`;
  const isValid = isValidPhone(full);

  // Tracks the submit region (the send, and the hack's /api/recipient); on success the page navigates
  // away, so this pair's loadFinish fires only on a failure settle — which is why the send reports its
  // own outcome separately.
  useLoadBi('phone-verification', { isLoading: submitting, isError: !!error });

  // Client-only — `navigator`/`Intl` region isn't available during SSR, so the server always renders
  // 'US' and this corrects it after hydration without a mismatch.
  useEffect(() => {
    setLocaleIso(defaultCountryIso(navigator.language || i18n?.language));
  }, [i18n?.language]);

  useEffect(() => {
    if (!digits) return undefined;
    const timer = setTimeout(() => setTouched(true), 600);
    return () => clearTimeout(timer);
  }, [digits]);

  // Both CTAs are disabled until the number validates, so someone who enters a number we reject
  // reaches no endpoint and fires no other event — leaving "gave no number" and "gave one we would
  // not accept" as the same silence in the funnel. Once per session; the digits are never sent.
  // A seeded field is non-empty before anyone types, which would report typing that never happened.
  const typingReported = useRef(Boolean(seededPhone));
  useEffect(() => {
    if (!digits || typingReported.current) return;
    typingReported.current = true;
    logPageClickBi({ ...PAGE, action: 'phone_input_started' });
  }, [digits, logPageClickBi]);

  async function enterApp() {
    // Wait for the just-created recipient to be visible to the home gate before navigating, or the
    // first attempt races the search index and bounces back to onboarding. Clear any prior
    // active-conversation pointer so the client's derived onboarding kickoff opens a clean thread.
    await waitForRecipientVisible(API_BASE);
    setActiveConversationId(null);
    router.push(`${basePath}/`);
  }

  async function send(deliveryOption: 'SMS' | 'WHATSAPP') {
    const isWhatsApp = deliveryOption === 'WHATSAPP';
    logPageClickBi({ ...PAGE, button_name: isWhatsApp ? 'send_whatsapp' : 'send' });
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    const failure = isWhatsApp ? t('onboarding.phone.error.whatsappUnavailable') : t('onboarding.phone.error.sendFailed');
    try {
      const { result: data, refused } = await sendVerificationCode(full, deliveryOption);
      // A refusal is not a failure, and neither of the ordinary messages is true about it: the budget is
      // shared with strangers, so this may be a first attempt, retrying will not clear it today, and the
      // other channel shares the same wall.
      if (refused) {
        logSendCode('refused', deliveryOption);
        logPageClickBi({ page_name: PAGE.page_name, action: 'Phone verification refused' });
        setError(t('onboarding.phone.error.sendUnavailable'));
        setSubmitting(false);
        return;
      }
      if (!data || !wasCodeSent(data, isWhatsApp)) {
        logSendCode('not-sent', deliveryOption);
        logPageClickBi({
          page_name: PAGE.page_name,
          action: isWhatsApp ? 'Phone verification failed: whatsapp unavailable' : 'Phone verification failed: send failed',
        });
        setError(failure);
        setSubmitting(false);
        return;
      }
      // Fired BEFORE the handover: both branches below leave this screen, and an event queued after a
      // navigation is a beacon nobody sends.
      logSendCode('sent', deliveryOption);
      // SMS carries the verificationId the code step verifies against; WhatsApp has none
      // (the recipient service checks the code itself), so it hands over none.
      const method = isWhatsApp ? 'whatsapp' : 'sms';
      const verificationId = !isWhatsApp && data.verificationId ? data.verificationId : '';
      if (onCodeSent) {
        onCodeSent({ phone: full, method, verificationId });
        return;
      }
      const params = new URLSearchParams({ phone: full, method });
      if (verificationId) params.set('verificationId', verificationId);
      router.push(`${basePath}/onboarding/code?${params.toString()}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logSendCode('failed', deliveryOption);
      logPageClickBi({ page_name: PAGE.page_name, action: `Phone verification failed: ${message}` });
      console.warn('[phone-verification]', deliveryOption, err);
      setError(failure);
      setSubmitting(false);
    }
  }

  return {
    countries,
    iso,
    setIso: setPickedIso,
    // BI for the picker is the SYMPHONY screen's to fire: Orion's native <select> emitted
    // none, and extracting this hook must not invent events on Orion's live funnel.
    //
    // The country picked rides `additionalFields`, NOT componentName. It used to be the
    // componentName, which made every country its own component — uncountable as one CTA, and
    // unjoinable to the native apps, which report a stable component with `country`/`country_name`
    // in the payload. The value is not lost by the move; it lands in the field the BI review asked
    // for. `country_name` is the localized display name, so a report can read a country without a
    // lookup table.
    logCountryPick: (next: string) =>
      logPageClickBi(
        { ...PAGE, button_name: 'country_code' },
        {
          additionalFields: {
            country: next,
            country_name: countries.find((c) => c.iso === next)?.name,
          },
        },
      ),
    logCountryListOpen: () => logPageClickBi({ ...PAGE, button_name: 'open_country_code' }),
    phone,
    setPhone,
    onBlur: () => setTouched(true),
    submitting,
    error,
    isValid,
    showInvalid: touched && digits.length > 0 && !isValid,
    sendSms: () => send('SMS'),
    sendWhatsApp: () => send('WHATSAPP'),
  };
}

export type PhoneVerification = ReturnType<typeof usePhoneVerification>;
